import { zodResolver } from '@hookform/resolvers/zod';
import { paths } from 'config/paths';
import { defaultApiErrorSchema, parseApiErrorMessage } from 'lib/api/utils';
import { useAcceptInvite } from 'lib/hooks/auth/auth.mutations';
import { useFetchInviteUserDetail } from 'lib/hooks/user/user.queries';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import InputMask from 'react-text-mask';
import { companyNameSchema } from 'schemas/common.schemas';
import { z } from 'zod';
import PasswordStrengthStatus from '../../components/PasswordStrengthStatus';
import Logo from '../../images/logo.png';
import './styles.scss';

const phoneNumberMask = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

const acceptInvitationFormSchema = z.object({
  companyname: companyNameSchema,
  firstname: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(25, 'First name cannot exceed 25 characters'),
  lastname: z
    .string()
    .trim()
    .min(1, 'Last name is required')
    .max(25, 'Last name cannot exceed 25 characters'),
  email: z
    .string()
    .trim()
    .email('Invalid email format')
    .min(1, 'Email is required')
    .max(50, 'Email cannot exceed 50 characters'),
  phone: z
    .string()
    .min(14, 'Phone number must be at least 10 digits long')
    .max(19, 'Phone number cannot exceed 15 digits'),
  // +4 is to account for input mask characters (123) 456-7890
  password: z.string().min(1, 'Password is required'),
});

type AcceptInvitationFormSchema = z.infer<typeof acceptInvitationFormSchema>;

export default function AcceptInvitationComponent() {
  const {
    register,
    formState: { errors, isSubmitting },
    ...form
  } = useForm<AcceptInvitationFormSchema>({
    resolver: zodResolver(acceptInvitationFormSchema),
    defaultValues: {
      companyname: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      password: '',
    },
  });
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const { data: inviteUserDetail, isLoading: inviteDetailsLoading } =
    useFetchInviteUserDetail({
      token,
    });
  const { mutateAsync: acceptInvite, isLoading: isAcceptInvitePending } =
    useAcceptInvite();

  useEffect(() => {
    document.title = 'Accept Invitation | UltraIoT';
  }, []);

  useEffect(() => {
    if (inviteUserDetail?.data) {
      form.reset({
        companyname: inviteUserDetail.data.companyname,
        firstname: inviteUserDetail.data.firstname,
        lastname: inviteUserDetail.data.lastname,
        email: inviteUserDetail.data.email,
        phone: inviteUserDetail.data.phone ?? '',
        password: '',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteUserDetail]);

  const handleAcceptInvite = async (data: AcceptInvitationFormSchema) => {
    if (!inviteUserDetail?.data?.id) return;

    try {
      const resp = await acceptInvite({
        id: inviteUserDetail.data?.id,
        ...data,
        phone: data.phone?.replace(/[^\d]/g, ''),
      });

      if (resp.success) {
        localStorage.setItem('Authorization', resp.token);
        history.push(paths.dashboard.path);
      }
    } catch (error) {
      const parsedError = defaultApiErrorSchema
        .extend({
          data: z.object({
            duplicateFields: z.array(
              z.object({
                field: z.enum(['companyname', 'email', 'phone']),
                message: z.string(),
              })
            ),
          }),
        })
        .safeParse(error);

      if (parsedError.success) {
        const duplicateFields = parsedError.data.data.duplicateFields;

        duplicateFields.forEach((field) => {
          form.setError(field.field, {
            message: field.message,
          });
        });
      } else {
        const errorMessage = parseApiErrorMessage(error);

        form.setError('root', {
          message: errorMessage ?? 'Something went wrong',
        });
      }
    }
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) return 'Please wait...';
    if (inviteDetailsLoading) return 'Loading...';
    return 'Accept Invitation';
  };

  const passwordValue = form.watch('password');

  const isDisabled =
    isSubmitting ||
    !inviteUserDetail?.data?.id ||
    inviteDetailsLoading ||
    isAcceptInvitePending;

  return (
    <div className="signup">
      <div className="signup-inner d-flex align-items-center">
        <div className="signup-body">
          <div className="card">
            <div className="card-body">
              <div className="card-inner-heading">
                <div className="logo">
                  <img src={Logo} alt="Ultra" />
                </div>

                <p className="subtitle">
                  Welcome to Ultra, fill out the form below to get setup
                </p>
              </div>

              <form onSubmit={form.handleSubmit(handleAcceptInvite)} noValidate>
                <div className="form-group material-textfield">
                  <input
                    type="text"
                    className="form-control form-control-lg material-textfield-input"
                    {...register('companyname')}
                    disabled
                    required
                  />
                  <label className="material-textfield-label">
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <span className="invalid-feedback d-block">
                    {errors.companyname?.message}
                  </span>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        {...register('firstname')}
                        required
                      />
                      <label className="material-textfield-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <span className="invalid-feedback d-block">
                        {errors.firstname?.message}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        {...register('lastname')}
                        required
                      />
                      <label className="material-textfield-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <span className="invalid-feedback d-block">
                        {errors.lastname?.message}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        autoComplete="off"
                        {...register('email')}
                        required
                      />
                      <label className="material-textfield-label">
                        Email Address <span className="text-danger">*</span>
                      </label>

                      <span className="invalid-feedback d-block">
                        {errors.email?.message}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group material-textfield">
                      <Controller
                        name="phone"
                        control={form.control}
                        render={({ field }) => (
                          <InputMask
                            {...field}
                            ref={null}
                            guide={false}
                            type="text"
                            id="phone"
                            keepCharPositions={false}
                            mask={phoneNumberMask}
                            className="form-control form-control-lg material-textfield-input"
                            required
                          />
                        )}
                      />
                      <label className="material-textfield-label">
                        Phone Number{' '}
                      </label>
                      <span className="invalid-feedback d-block">
                        {errors.phone?.message}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="form-group material-textfield">
                  <input
                    type="password"
                    className="form-control form-control-lg material-textfield-input"
                    {...register('password')}
                    required
                  />
                  <label className="material-textfield-label">Password</label>
                  <span className="invalid-feedback d-block">
                    {errors.password?.message}
                  </span>
                </div>

                <PasswordStrengthStatus password={passwordValue} />

                <div className="invalid-feedback text-center mb-3">
                  {errors.root?.message}
                </div>

                <button
                  disabled={isDisabled}
                  type="submit"
                  className="btn btn-dark btn-lg w-100"
                >
                  {getSubmitButtonText()}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
